import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";

import { useSingleUser, useUserReputationScore } from "@/hooks";
import MerchantHeader from "@/components/p2p/merchant/MerchantHeader";
import MerchantOverview from "@/components/p2p/merchant/MerchantOverview";
import MerchantReviews from "@/components/p2p/merchant/MerchantReviews";
import MerchantAdvertisements from "@/components/p2p/merchant/MerchantAdvertisements";
import { useEffect, useRef } from "react";

export default function MerchantProfilePage() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const { data: userScore } = useUserReputationScore(merchantId ?? "");

  const [searchParams, setSearchParams] = useSearchParams();
  const tabsRef = useRef<HTMLDivElement>(null);

  const tab = searchParams.get("tab") ?? "overview";

  useEffect(() => {
    if (tab === "ads") {
      requestAnimationFrame(() => {
        tabsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  }, [tab]);

  const {
    data: merchant,
    isLoading,
    isError,
    refetch,
  } = useSingleUser(merchantId!);

  console.log("MERCHANT", merchant);

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError || !merchant) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-1 md:px-6 py-6 space-y-6 mt-10">
        {/* Back */}

        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="gap-2 hidden sm:flex"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        {/* Hero */}
        <MerchantHeader
          merchantId={merchant.id}
          onViewAds={() => setSearchParams({ tab: "ads" })}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Main */}
          <div ref={tabsRef} className="xl:col-span-9">
            <Card className="rounded-3xl shadow-sm">
              <Tabs
                value={tab}
                onValueChange={(value) => setSearchParams({ tab: value })}
                className="w-full"
              >
                <div className="p-2">
                  <TabsList className="flex items-center justify-between gap-4 w-full h-14">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="reviews">Reviews</TabsTrigger>
                    <TabsTrigger value="ads">Active Ads</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview" className="p-1">
                  <MerchantOverview merchantId={merchant.id} />
                </TabsContent>

                <TabsContent value="reviews" className="p-1">
                  <MerchantReviews merchantId={merchant.id} />
                </TabsContent>

                <TabsContent value="ads" className="p-1">
                  <MerchantAdvertisements merchantId={merchant.id} />
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="hidden xl:block xl:col-span-3">
            <Card className="sticky top-6 rounded-3xl">
              <div className="p-6 space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Completion Rate
                  </p>

                  <h2 className="text-3xl font-bold mt-1">
                    {userScore?.completionRate}%
                  </h2>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Completed Trades
                  </p>

                  <h2 className="text-2xl font-semibold">
                    {userScore?.completedTrades}
                  </h2>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Average Release Time
                  </p>

                  <h2 className="text-xl font-semibold">
                    {userScore?.averageReleaseTime} minutes
                  </h2>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Response Time</p>

                  <h2 className="text-xl font-semibold">
                    {userScore?.averageResponseTime} minutes
                  </h2>
                </div>

                <Button size="lg" className="w-full rounded-xl">
                  Trade With Merchant
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
